import { BookOpen, Scale, Info, Search, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

interface LawsTaskGuideProps {
  onNavigateToResources: () => void;
  onCloseWizard: () => void;
}

export function LawsTaskGuide({ onNavigateToResources, onCloseWizard }: LawsTaskGuideProps) {
  const handleGoToResources = () => {
    onNavigateToResources();
    onCloseWizard();
  };

  const resources = [
    {
      name: 'MPEP',
      icon: BookOpen,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      description: 'Manual of Patent Examining Procedure',
      examples: ['2106 (Subject Matter Eligibility)', '2141 (Obviousness)', '2173.05 (Written Description)']
    },
    {
      name: '37 CFR',
      icon: Info,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      description: 'Code of Federal Regulations',
      examples: ['1.1 (Fees)', '1.53 (Applications)', '1.111 (Replies)']
    },
    {
      name: '35 USC',
      icon: Scale,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      description: 'United States Code',
      examples: ['101 (Eligibility)', '102 (Novelty)', '103 (Obviousness)', '112 (Specification)']
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Look Up Laws & Guidance</h2>
        <p className="text-gray-600">
          Search and view patent regulations instantly
        </p>
      </div>

      <div className="space-y-4">
        {/* Navigate to Resources */}
        <div className="border-2 border-teal-200 rounded-lg p-5 bg-teal-50">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              1
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Open the Resources Tab</h3>
              <p className="text-sm text-gray-600 mb-3">
                Access all patent laws and guidance in one place
              </p>
              <Button
                onClick={handleGoToResources}
                className="gap-2 bg-teal-600 hover:bg-teal-700"
                size="sm"
              >
                <BookOpen className="w-4 h-4" />
                Go to Resources
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* What's Available */}
        <div className="bg-white rounded-lg p-5 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-600" />
            What You Can Search
          </h3>
          <div className="space-y-4">
            {resources.map((resource, idx) => {
              const Icon = resource.icon;
              return (
                <div key={idx} className={`${resource.bg} rounded-lg p-4 border border-gray-200`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Icon className={`w-5 h-5 ${resource.color}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{resource.name}</h4>
                      <p className="text-xs text-gray-600 mb-2">{resource.description}</p>
                      <div className="text-xs text-gray-700">
                        <span className="font-medium">Try: </span>
                        {resource.examples.map((ex, i) => (
                          <span key={i}>
                            {ex}
                            {i < resource.examples.length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pro Tip */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
          <p className="text-sm text-gray-700">
            <strong className="text-amber-900">💡 Pro Tip:</strong> You can also add MPEP, CFR, or USC
            sections directly to your session using the "Add Documents" button, then ask questions about them!
          </p>
        </div>
      </div>
    </div>
  );
}
